import { useMemo, useState } from 'react';
import { Button, Drawer, Grid, Space } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import SearchInput from '../../../../Utils/SearchInput';
import { useApprovalCount, useUpdateUserStatus } from '../../../../Hooks/TT_ELD/NewAccounts';
import AcceptedListTable from './CustomTable/AcceptedListTable';

const { useBreakpoint } = Grid;

type AcceptedProps = {
  companyName: string;
  setCompanyName: (value: string) => void;
  username: string;
  setUsername: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  skip: string;
  setSkip: (value: string) => void;
};

const AcceptedList = ({
  companyName,
  setCompanyName,
  username,
  setUsername,
  email,
  setEmail,
  skip,
  setSkip,
}: AcceptedProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      companyName: setCompanyName,
      email: setEmail,
      username: setUsername,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
      setSkip('1');
    }
  };

  const { data, refetch, isLoading, isFetching } = useUpdateUserStatus(
    username,
    email,
    companyName,
    skip,
    'accepted',
  );

  const count = useApprovalCount();

  const onChange = (query: any) => setSkip(query.current);

  const updates = () => {
    refetch();
    count.refetch();
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <SearchInput
          value={companyName}
          onDebounceChange={(event: string) => handleChange(event, 'companyName')}
          placeholder="Search by company"
        />
        <SearchInput
          value={username}
          onDebounceChange={(event: string) => handleChange(event, 'username')}
          placeholder="Search by name"
        />
        <SearchInput
          value={email}
          onDebounceChange={(event: string) => handleChange(event, 'email')}
          placeholder="Search by email"
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                updates();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [companyName, username, email, isMobile],
  );

  return (
    <div className="mainPage">
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SearchInput
              value={companyName}
              onDebounceChange={(event: string) => handleChange(event, 'companyName')}
              placeholder="Search by company"
            />
            <SearchInput
              value={username}
              onDebounceChange={(event: string) => handleChange(event, 'username')}
              placeholder="Search by name"
            />
            <SearchInput
              value={email}
              onDebounceChange={(event: string) => handleChange(event, 'email')}
              placeholder="Search by email"
            />
          </div>

          <Button size="large" onClick={updates}>
            Refresh
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block size="large" onClick={updates}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      <AcceptedListTable
        data={data?.data}
        onChange={onChange}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={updates}
        skip={skip}
      />
    </div>
  );
};

export default AcceptedList;
